import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { TIME_WINDOWS, SAMPLE_RATES, THEME_OPTIONS, CHART_THEMES, bufferSizeFor, getGhostColor, type ChartConfig, type ThemeOption } from './chart-types';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-chart-config-dialog> — a chart's settings: time window · sample
 * rate · y-axis range · theme · name · ghost management · a summary of what is
 * in force. The v2 ChartConfigDialog, in the app's top-layer <vitesse-dialog>.
 *
 * Preview-vs-save: every edit emits `config-preview` so the live chart reflects
 * it immediately; `config-save` persists the working copy; cancel/close reverts
 * the live preview to the snapshot taken on open. Light DOM, global SCSS, no
 * inline styles except the two theme gradients (data, not chrome), fully i18n'd.
 *
 * The theme picker is the v2 "typewriter": the names sit in one bordered row,
 * the chosen one bold with its gradient underlined beneath it, and a pick fades
 * the old choice out for a beat before the underline slides in under the new.
 */
@customElement('vitesse-chart-config-dialog')
export class VitesseChartConfigDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ attribute: false }) config!: ChartConfig;
  /** What the chart is called when it has no custom name — the heading and the name placeholder. */
  @property() label = '';

  @state() private working!: ChartConfig;
  @state() private themeFading = false;
  private original!: ChartConfig;

  constructor() {
    super();
    new LocaleController(this);
  }
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('open') && this.open) {
      this.original = { ...this.config, theme: this.config.theme ?? 'frost' };
      this.working = { ...this.original };
    }
  }

  private patch(p: Partial<ChartConfig>): void {
    this.working = { ...this.working, ...p };
    this.emit('config-preview', this.working);
  }
  private emit(name: 'config-preview' | 'config-save', detail: ChartConfig): void {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  private save(): void {
    this.emit('config-save', this.working);
  }
  private cancel(): void {
    this.emit('config-preview', this.original); // revert the live preview
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  }

  private pickTheme(theme: ThemeOption): void {
    if (theme === this.working.theme) return;
    this.themeFading = true;
    setTimeout(() => {
      this.patch({ theme });
      this.themeFading = false;
    }, 150);
  }

  private deleteGhost(id: string): void {
    this.patch({ ghosts: (this.working.ghosts ?? []).filter((g) => g.id !== id) });
  }

  render() {
    if (!this.working) return nothing;
    const c = this.working;
    const ghosts = c.ghosts ?? [];
    const theme = c.theme ?? 'frost';
    const palette = CHART_THEMES[theme] ?? CHART_THEMES.frost;
    const rate = SAMPLE_RATES.find((r) => r.value === c.sampleRateHz);
    const gradient = (th: ThemeOption): string => {
      const p = CHART_THEMES[th] ?? CHART_THEMES.frost;
      return `background:linear-gradient(to right, ${p.gradient[0]}, ${p.gradient[1]})`;
    };
    return html`<vitesse-dialog
      .open=${this.open}
      heading=${t('chart.settingsFor', { name: this.label })}
      closeLabel=${t('chart.cancel')}
      width="560px"
      @dialog-close=${() => this.cancel()}
    >
      <div class="dialog-form chart-config">
        <div class="form-group">
          <label>${t('chart.timeWindow')}</label>
          <select class="config-select" .value=${String(c.timeWindowMs)}
            @change=${(e: Event) => this.patch({ timeWindowMs: Number((e.target as HTMLSelectElement).value) })}>
            ${TIME_WINDOWS.map((w) => html`<option value=${w.value} ?selected=${w.value === c.timeWindowMs}>${t('chart.lastWindow', { window: w.label })}</option>`)}
          </select>
          <div class="field-help">${t('chart.timeWindowHelp')}</div>
        </div>

        <div class="form-group">
          <label>${t('chart.sampleRate')}</label>
          <select class="config-select" .value=${String(c.sampleRateHz)}
            @change=${(e: Event) => this.patch({ sampleRateHz: Number((e.target as HTMLSelectElement).value) })}>
            ${SAMPLE_RATES.map((r) => html`<option value=${r.value} ?selected=${r.value === c.sampleRateHz}>${t('chart.sampleRateOption', { rate: r.label, interval: r.interval })}</option>`)}
          </select>
          <div class="field-help">${t('chart.sampleRateHelp')}</div>
        </div>

        <div class="form-group">
          <div class="config-yaxis">
            <div class="config-yaxis-field">
              <label>${t('chart.yAxisMin')}</label>
              <input class="config-input" type="number" step="0.1" placeholder=${t('chart.auto')}
                .value=${c.yAxisMin == null ? '' : String(c.yAxisMin)}
                @input=${(e: Event) => this.patch({ yAxisMin: numOrUndef((e.target as HTMLInputElement).value) })} />
            </div>
            <div class="config-yaxis-field">
              <label>${t('chart.yAxisMax')}</label>
              <input class="config-input" type="number" step="0.1" placeholder=${t('chart.auto')}
                .value=${c.yAxisMax == null ? '' : String(c.yAxisMax)}
                @input=${(e: Event) => this.patch({ yAxisMax: numOrUndef((e.target as HTMLInputElement).value) })} />
            </div>
          </div>
          <div class="field-help">${t('chart.yAxisHelp')}</div>
        </div>

        <div class="form-group">
          <label>${t('chart.theme')}</label>
          <div class="theme-typewriter">
            ${THEME_OPTIONS.map(
              (th) => html`<button
                class="theme-typewriter-option ${theme === th ? 'selected' : ''} ${this.themeFading && theme === th ? 'fading' : ''}"
                @click=${() => this.pickTheme(th)}
              >
                <span>${t(`chartTheme.${th}` as 'chartTheme.frost')}</span>
                ${theme === th ? html`<span class="theme-typewriter-underline" style=${gradient(th)}></span>` : nothing}
              </button>`,
            )}
          </div>
        </div>

        <div class="form-group">
          <label>${t('chart.chartName')}</label>
          <input class="config-input" type="text" maxlength="50" placeholder=${t('chart.chartNamePlaceholder')}
            .value=${c.customName ?? ''}
            @input=${(e: Event) => this.patch({ customName: (e.target as HTMLInputElement).value || undefined })} />
          <div class="field-help">${t('chart.chartNameHelp')}</div>
        </div>

        ${ghosts.length > 0
          ? html`<div class="form-section">
              <h3>${t('chart.ghostSnapshots')}</h3>
              <div class="ghost-list">
                ${ghosts.map((g, i) => {
                  const d = new Date(g.capturedAt);
                  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                  const win = g.timeWindowMs >= 60_000 ? `${(g.timeWindowMs / 60_000).toFixed(0)}m` : `${(g.timeWindowMs / 1000).toFixed(0)}s`;
                  return html`<div class="ghost-list-item">
                    <span class="ghost-color-indicator" style=${`background:${getGhostColor(i)}`}></span>
                    <div class="ghost-item-info">
                      <span class="ghost-item-label">${t('chart.ghostItem', { n: i + 1 })}</span>
                      <span class="ghost-item-meta">${t('chart.ghostItemMeta', { time, window: win, n: g.data.length })}</span>
                    </div>
                    <button class="ghost-delete-button" @click=${() => this.deleteGhost(g.id)} title=${t('chart.deleteGhost')}>
                      <vitesse-icon name="trash-2" size="14"></vitesse-icon>
                    </button>
                  </div>`;
                })}
              </div>
              <div class="ghost-actions-footer">
                ${button({ label: t('chart.deleteAll'), color: 'red', onClick: () => this.patch({ ghosts: [] }) })}
              </div>
            </div>`
          : nothing}

        <div class="form-section">
          <h3>${t('chart.currentSettings')}</h3>
          <div class="settings-summary">
            <div class="summary-row">
              <span class="summary-label">${t('chart.displayName')}</span>
              <span class="summary-value">${c.customName || t('chart.autoName')}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">${t('chart.sampleRateLabel')}</span>
              <span class="summary-value">${rate ? `${rate.label} (${rate.interval})` : '—'}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">${t('chart.bufferSize')}</span>
              <span class="summary-value">${t('chart.bufferPoints', { n: bufferSizeFor(c) })}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">${t('chart.themeLabel')}</span>
              <div class="summary-theme">
                <span class="summary-value">${t(`chartTheme.${theme}` as 'chartTheme.frost')}</span>
                <span class="theme-preview-line" style=${`background:linear-gradient(to right, ${palette.gradient[0]}, ${palette.gradient[1]})`}></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer">
        ${button({ label: t('chart.cancel'), color: 'neutral', onClick: () => this.cancel() })}
        ${button({ label: t('chart.saveSettings'), color: 'blue', onClick: () => this.save() })}
      </div>
    </vitesse-dialog>`;
  }
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function numOrUndef(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-chart-config-dialog': VitesseChartConfigDialog;
  }
}
